import { LoadState } from '@/ui/LoadState';
/**
 * CommunitiesPanel — right-rail recommended communities.
 *
 * Reads useCommunitiesRecommended().communities; falls back to
 * useCommunities().communities.slice(0, 3) when the recommended list is empty.
 *
 * Each row's Join affordance calls toggleJoin from useCommunities().
 */
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Panel, SectionLabel, Button } from '@/ui';
import { useCommunitiesRecommended, useCommunities, useRequireAuth } from '@/hooks';
import { PanelEmpty } from '@/components/panels/PanelEmpty';
import type { Community } from '@/core/models';

/* ── Styled helpers ───────────────────────────────────────────────────────── */

const ViewAll = styled(Link)`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const CommunityName = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Members = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* ── Inner component ──────────────────────────────────────────────────────── */

interface CommunityRowProps {
  community: Community;
  toggleJoin: (id: string) => Promise<void>;
}

function CommunityRow({ community, toggleJoin }: CommunityRowProps) {
  const requireAuth = useRequireAuth();

  function handleJoin() {
    if (!requireAuth('join')) return;
    void toggleJoin(community.id);
  }

  return (
    <Row>
      <Info>
        <CommunityName>{community.name}</CommunityName>
        <Members>{community.members}</Members>
      </Info>
      <Button
        /* A repeated row action, not the page's primary action. It used to be
           'primary', so three filled accent buttons stacked in one rail panel
           while the sibling Who-to-Follow panel used 'outline' for the exact
           same kind of control — two components written independently and never
           looked at side by side. Both now use 'outline', and the one accent
           fill on the page belongs to Post. */
        variant={community.joined ? 'secondary' : 'outline'}
        onClick={handleJoin}
        aria-label={`${community.joined ? 'Joined' : 'Join'} ${community.name}`}
      >
        {community.joined ? 'Joined' : 'Join'}
      </Button>
    </Row>
  );
}

/* ── Panel ────────────────────────────────────────────────────────────────── */

export function CommunitiesPanel() {
  const { communities: recommended, ...status } = useCommunitiesRecommended();
  const { communities: all, toggleJoin } = useCommunities();

  const display = recommended.length > 0 ? recommended : all.slice(0, 3);

  return (
    <Panel>
      <SectionLabel
        as="h2"
        action={
          <ViewAll to="/communities" aria-label="View all communities">
            View all
          </ViewAll>
        }
      >
        Communities
      </SectionLabel>
      {status.loading || status.error ? (
        <LoadState {...status} label="communities" />
      ) : display.length === 0 ? (
        <PanelEmpty>No communities to show yet.</PanelEmpty>
      ) : (
        <List>
          {display.map((community) => (
            <CommunityRow key={community.id} community={community} toggleJoin={toggleJoin} />
          ))}
        </List>
      )}
    </Panel>
  );
}
